import type { BillableFormPluginProps, SaveCallbackData } from '@domains/subscription/priv/Billables/usecases/BillableForm/plugin'
import { RangeLabel } from '@domains/subscription/priv/Billables/usecases/Price/tiered/components/RangeLabel'
import type { TieredPrice } from '@domains/subscription/priv/Billables/usecases/Price/tiered/tiered'
import type { BaseAlgorithm } from '@domains/subscription/priv/components/Confirmable/confirmable'
import { Stack, Typography } from '@mui/material'
import { useRef } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'

export interface TieredPricePlugin<T extends BaseAlgorithm> {
  supports: (type: string) => boolean
  Component: React.ComponentType<TieredBillableFormProps<T>>
}

export interface TieredBillableFormProps<T extends BaseAlgorithm> extends BillableFormPluginProps {
  tieredPrice: TieredPrice<T> | undefined
  onSubmit: (data: TieredPrice<T>) => Promise<void>
}

export const TieredPriceRow = ({
  tieredPrice,
  onRegisterSave,
  onSubmit,
  additionalPlugins,
  templateId,
  unit,
}: TieredBillableFormProps<BaseAlgorithm>) => {
  const form = useForm<TieredPrice<BaseAlgorithm>>({
    values: tieredPrice,
  })

  const allChangesRef = useRef<Record<string, SaveCallbackData>>({})

  const tiers = useFieldArray({ control: form.control, name: 'tiers' })

  const submitForm = async (values: TieredPrice<BaseAlgorithm>) => {
    await onSubmit(values).then(() => {
      onRegisterSave({
        changes: [],
        save: () => form.handleSubmit(submitForm)(),
      })
    })
  }

  const handleFieldChanges = (tierId: string, callback: SaveCallbackData) => {
    allChangesRef.current[tierId] = callback

    const allChanges = Object.values(allChangesRef.current).flatMap((c) => c.changes)
    const callbacks = Object.values(allChangesRef.current).map((c) => c.save)

    onRegisterSave({
      changes: allChanges,
      save: () => {
        callbacks.forEach((save) => save())

        return form.handleSubmit(submitForm)()
      },
    })
  }

  const pluginComponents = tiers.fields.map((tier, index) => {
    const plugin = additionalPlugins?.find((plugin) => plugin.supports(tier.algorithm))

    if (!plugin || !plugin.FormComponent) {
      return (
        <Stack
          data-testid="missing-tier-plugin"
          key={tier.id}
          sx={{
            gap: 2,
          }}
        >
          <RangeLabel from={tier.range.fromExclusive} to={tier.range.toInclusive} unit={unit} />
          <Typography key={tier.id} variant="p4Regular" sx={{ color: 'red' }}>
            Backoffice does not support '{tier.algorithm.type}' price types in this configuration
          </Typography>
        </Stack>
      )
    }

    return (
      <Stack
        data-testid="tier-plugin"
        key={tier.id}
        sx={{
          gap: 2,
        }}
      >
        <RangeLabel from={tier.range.fromExclusive} to={tier.range.toInclusive} unit={unit} />
        <plugin.FormComponent
          price={tier.algorithm}
          onRegisterSave={(changes) => handleFieldChanges(tier.id, changes)}
          additionalPlugins={additionalPlugins}
          templateId={templateId}
          unit={unit}
          onDataChange={(data) => form.setValue(`tiers.${index}.algorithm`, data)}
        />
      </Stack>
    )
  })

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      {pluginComponents}
    </Stack>
  )
}

const noop = () => {}
